import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './StandardRateSelector.module.scss';
import { Radio } from 'components/UI/V3/Forms/Radio';
import { PickDropHourSelector } from '../PickDropHourSelector/PickDropHourSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import UnitSelector, { UnitBadge } from '../UnitSelector/UnitSelector';
import { BoardingService, BoardingServiceWrapper, getBoardingSelectableNights } from 'model/Service';
import { Moment } from 'moment-timezone';
import { Room, RoomRule, RoomRuleTypeIds, RoomUnit } from '@spike/room-model';
import { useDispatch, useSelector } from 'react-redux';
import { showWarningThunk } from '@spike/notifications-action';
import Spinner from 'components/UI/V3/Spinner';
import { RootState } from 'store';
import { fetchBoardingOffersThunk } from 'actions/services/ServicesActions';
import { canFetchBoardingOffers } from 'utils/boardingOfferDates';
import { PetsState } from '@spike/pets-action';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import useNonInitialEffect from '@versiondos/hooks';
import { getCapacityInfo } from 'components/CreateBooking/Daycare/Utils/GetCapacityInfo';
import { getWeightRangeInfo } from 'components/CreateBooking/Daycare/Utils/GetWeightRangeInfo';
import AlertMessage from 'components/UI/V3/AlertMessage';
import Notes from 'components/CreateBooking/Notes';
import PriceField from 'components/UI/V3/Forms/PriceField/PriceField';
import { useApiClientWrapper, usePermissions } from 'hooks';
import lockStyles from 'components/CreateBooking/UI/priceFieldInteractionLock.module.scss';
import { MembershipItem } from '@spike/membership-model';
import {
    getBoardingAppliedOfferForStandardPricing,
    getBoardingCatalogNightlyPrice,
    getBoardingStandardNightlyPrice,
    hasBoardingRateRuleApplied
} from '../../utils/boardingPricing';
import {
    isMembershipNightlyHigherThanAppliedRate,
    resolveBoardingNightlyPriceFromService
} from 'components/CreateBooking/utils/membershipDisplayPrice';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';
import BoardingUnitSelectorSection from '../BoardingUnitSelectorSection/BoardingUnitSelectorSection';
import SaveCustomPriceCheckbox from 'components/CreateBooking/UI/SaveCustomPriceCheckbox';
import SavedCustomPriceChoice from 'components/CreateBooking/UI/SavedCustomPriceChoice';
import { useSavedCustomPriceChoice } from 'components/CreateBooking/UI/useSavedCustomPriceChoice';

interface StandardRateSelectorProps {
    boardingServices: Array<BoardingServiceWrapper>;
    petId: number;
    from: Moment;
    to: Moment;
    nights?: number;
    selectedService?: BoardingServiceWrapper;
    room?: Room;
    roomUnitId?: number;
    nightlyPrice?: number;
    nightlyPriceError?: string;
    saveCustomPrice?: boolean;
    useSavedCustomPrice?: boolean;
    onSelect: (boardingService: BoardingServiceWrapper) => void;
    onNightlyPriceChange: (price: number) => void;
    onSaveCustomPriceChange?: (checked: boolean) => void;
    onUseSavedCustomPriceChange?: (useSaved: boolean, price: number, customPriceId?: number) => void;
    onChangePeriod: (from: Moment, to: Moment) => void;
    onSelectRoomUnitId: (roomUnitId: number | undefined) => void;
    notes?: string;
    onNotesChange?: (notes: string) => void;
    isLoading?: boolean;
    className?: string;
    membershipItem?: MembershipItem;
    membershipBoardingServiceId?: number;
}

type CapacityType = 'weight' | 'size' | 'quantity' | 'weightRange' | null;

type CapacityInfo = {
    type: CapacityType;
    max?: number;
};

type UnitIndicator = UnitBadge & {
    remainingCapacity: number;
    includedInRoomSummary: boolean;
};

type RoomIndicator = {
    text: string;
    showWarningIcon: boolean;
};

const SIZE_MISMATCH_WARNING_TEXT = 'This room does not allow pets of this size.';

const isBySizeInvalidUnit = (unit: RoomUnit): boolean => {
    const eligibility = unit.sizeEligibility;
    return eligibility === 'mismatch' || eligibility === 'inconsistent';
};

const isBySizeRoom = (units: Array<RoomUnit>): boolean =>
    units.some(unit => unit.sizeEligibility !== null && unit.sizeEligibility !== undefined);

const getBySizeRuleData = (rule?: RoomRule): Array<{ petTypeId?: string; sizeId?: string; quantity?: number }> => {
    if (!rule?.data) return [];

    try {
        const parsed = JSON.parse(rule.data);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const getBySizeCapacity = (room: Room, unit: RoomUnit, pet?: PetsState['list'][number]): number | undefined => {
    const petTypeId = pet?.type?.plural?.toLowerCase();
    const petSizeId = pet?.size?.id;

    if (!petTypeId || !petSizeId) return undefined;

    const matchesPet = (ruleData: { petTypeId?: string; sizeId?: string; quantity?: number }): boolean =>
        String(ruleData.petTypeId) === String(petTypeId) && String(ruleData.sizeId) === String(petSizeId);

    const activeRule = room.rules.find(
        rule => rule.ruleTypeId === RoomRuleTypeIds.petSizeRuleId && String(rule.id) === String(unit.activeSizeRuleId)
    );

    const activeRuleQuantity = getBySizeRuleData(activeRule).find(matchesPet)?.quantity;

    if (activeRuleQuantity != null) {
        return activeRuleQuantity;
    }

    const fallbackRule = room.rules.find(
        rule => rule.ruleTypeId === RoomRuleTypeIds.petSizeRuleId && getBySizeRuleData(rule).some(matchesPet)
    );

    return getBySizeRuleData(fallbackRule).find(matchesPet)?.quantity;
};

export const StandardRateSelector: FunctionComponent<StandardRateSelectorProps> = ({
    boardingServices,
    petId,
    from,
    to,
    nights,
    selectedService,
    room,
    roomUnitId,
    nightlyPrice,
    nightlyPriceError,
    saveCustomPrice,
    useSavedCustomPrice,
    onSelect,
    onNightlyPriceChange,
    onSaveCustomPriceChange,
    onUseSavedCustomPriceChange,
    onChangePeriod,
    onSelectRoomUnitId,
    notes,
    onNotesChange,
    isLoading,
    className,
    membershipItem,
    membershipBoardingServiceId
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const permissions = usePermissions();
    const canEditPrices = permissions.checkout.editPrices;
    const catalogNightlyPrice = selectedService ? getBoardingCatalogNightlyPrice(selectedService) : 0;
    const savedCustomPrice = useSavedCustomPriceChoice({
        petId,
        serviceId: selectedService?.serviceId,
        catalogPrice: catalogNightlyPrice,
        currentPrice: Number(nightlyPrice) || 0,
        useSavedCustomPrice
    });

    const petsState = useSelector<RootState, PetsState>(state => state.pets);
    const pet = petsState.list.find(pet => pet.id === petId);

    const [selectedRoomUnit, setSelectedRoomUnit] = useState<RoomUnit | undefined>(undefined);

    const currentRoom: Room | undefined = room || selectedService?.rooms?.[0];
    const normalizedUnits = currentRoom ? currentRoom.units : [];
    const hasBySize = isBySizeRoom(normalizedUnits);

    const capacityInfo = useMemo<CapacityInfo>(() => getCapacityInfo(currentRoom), [currentRoom]);

    const handlePeriodChange = useCallback(
        (newFrom: Moment, newTo: Moment) => {
            const datesChanged =
                !newFrom.clone().startOf('day').isSame(from.clone().startOf('day'), 'day') ||
                !newTo.clone().startOf('day').isSame(to.clone().startOf('day'), 'day');

            onChangePeriod(newFrom, newTo);

            if (datesChanged && canFetchBoardingOffers(petId, newFrom, newTo)) {
                dispatch(
                    fetchBoardingOffersThunk(
                        petId,
                        newFrom.format('YYYY-MM-DD'),
                        newTo.format('YYYY-MM-DD'),
                        apiClientWrapper.marketplaceId
                    )
                );
            }
        },
        [dispatch, petId, from, to, onChangePeriod, apiClientWrapper.marketplaceId]
    );

    useEffect(() => {
        if (!currentRoom || !selectedRoomUnit) return;

        const updated = normalizedUnits.find(u => u.id === selectedRoomUnit.id);
        if (updated) {
            setSelectedRoomUnit(updated);
        }
    }, [currentRoom, normalizedUnits]);

    useEffect(() => {
        if (!currentRoom || normalizedUnits.length === 0) return;

        if (roomUnitId == null) {
            return;
        }

        const preUnit = normalizedUnits.find(u => u.id === roomUnitId);
        setSelectedRoomUnit(preUnit);
    }, [currentRoom, roomUnitId, normalizedUnits]);

    const prevServiceId = useRef<number | undefined>(undefined);

    useEffect(() => {
        if (!selectedService) return;

        if (prevServiceId.current && prevServiceId.current !== selectedService.serviceId) {
            setSelectedRoomUnit(undefined);
            onSelectRoomUnitId(undefined);
        }

        prevServiceId.current = selectedService.serviceId;
    }, [selectedService?.serviceId]);

    useNonInitialEffect(() => {
        if (!selectedService || !currentRoom || currentRoom.rules[0]?.ruleTypeId !== 4) return;

        const range = getWeightRangeInfo(currentRoom);
        if (!range) return;

        const petWeight = pet?.weight;
        if (!petWeight || petWeight <= 0) return;

        const outOfRange =
            (range.min !== undefined && petWeight < range.min) || (range.max !== undefined && petWeight > range.max);

        if (outOfRange) {
            dispatch(showWarningThunk('This room does not allow pets of this weight.'));
        }
    }, [currentRoom]);

    const getRuleWarningType = (roomToEvaluate?: Room): 'weight' | null => {
        if (!roomToEvaluate) return null;

        if (capacityInfo.type === 'weight' || capacityInfo.type === 'weightRange') {
            const petWeight = pet?.weight;

            if (!petWeight || petWeight <= 0) {
                return 'weight';
            }

            if (capacityInfo.type === 'weightRange') {
                const range = getWeightRangeInfo(roomToEvaluate);
                if (
                    range &&
                    ((range.min != null && petWeight < range.min) || (range.max != null && petWeight > range.max))
                ) {
                    return 'weight';
                }
            }
        }

        return null;
    };

    const weightRuleWarning = useMemo(
        () => getRuleWarningType(currentRoom),
        [currentRoom, capacityInfo.type, pet?.weight]
    );

    const unitIndicators = useMemo(() => {
        const indicators = new Map<number, UnitIndicator>();

        normalizedUnits.forEach(unit => {
            if (unit.id == null) {
                return;
            }

            if (unit.active === false) {
                indicators.set(unit.id, {
                    text: 'Unavailable',
                    showWarningIcon: true,
                    remainingCapacity: 0,
                    includedInRoomSummary: false
                });
                return;
            }

            if (weightRuleWarning === 'weight') {
                indicators.set(unit.id, {
                    text: 'By Weight',
                    showWarningIcon: true,
                    remainingCapacity: 0,
                    includedInRoomSummary: false
                });
                return;
            }

            if (hasBySize) {
                if (!currentRoom || isBySizeInvalidUnit(unit)) {
                    indicators.set(unit.id, {
                        text: 'By Size',
                        showWarningIcon: true,
                        remainingCapacity: 0,
                        includedInRoomSummary: false
                    });
                    return;
                }

                const max = getBySizeCapacity(currentRoom, unit, pet);
                const occupied = unit.occupancy ?? 0;
                const rawRemaining = max != null ? max - occupied : unit.available ? 1 : 0;
                const remainingCapacity = unit.available ? rawRemaining : Math.min(rawRemaining, 0);

                indicators.set(unit.id, {
                    text: max != null ? `${remainingCapacity}/${max}` : unit.available ? 'Available' : 'Unavailable',
                    showWarningIcon: remainingCapacity < 1,
                    remainingCapacity,
                    includedInRoomSummary: true
                });
                return;
            }

            const occupied = unit.occupancy ?? 0;

            if (capacityInfo.type === 'weight') {
                const petWeight = pet?.weight ?? 0;
                const max = capacityInfo.max ?? 0;

                if (!petWeight || petWeight <= 0 || !max) {
                    indicators.set(unit.id, {
                        text: 'By Weight',
                        showWarningIcon: true,
                        remainingCapacity: 0,
                        includedInRoomSummary: false
                    });
                    return;
                }

                const rawRemainingWeight = max - occupied;
                const remainingWeight = unit.available === false ? Math.min(rawRemainingWeight, 0) : rawRemainingWeight;
                const remainingCapacity = Math.floor(remainingWeight / petWeight);

                indicators.set(unit.id, {
                    text: `${remainingWeight}/${max} lbs`,
                    showWarningIcon: remainingCapacity < 1,
                    remainingCapacity,
                    includedInRoomSummary: true
                });
                return;
            }

            if (capacityInfo.max != null) {
                const rawRemainingCapacity = capacityInfo.max - occupied;
                const remainingCapacity =
                    unit.available === false ? Math.min(rawRemainingCapacity, 0) : rawRemainingCapacity;

                indicators.set(unit.id, {
                    text: `${remainingCapacity}/${capacityInfo.max}`,
                    showWarningIcon: remainingCapacity < 1,
                    remainingCapacity,
                    includedInRoomSummary: true
                });
                return;
            }

            const canAccommodate = unit.available !== false;

            indicators.set(unit.id, {
                text: canAccommodate ? 'Available' : 'Unavailable',
                showWarningIcon: !canAccommodate,
                remainingCapacity: canAccommodate ? 1 : 0,
                includedInRoomSummary: true
            });
        });

        return indicators;
    }, [normalizedUnits, weightRuleWarning, hasBySize, currentRoom, pet, capacityInfo]);

    const roomIndicator = useMemo<RoomIndicator | null>(() => {
        if (!currentRoom) {
            return null;
        }

        const enabledUnits = normalizedUnits.filter(unit => unit.active !== false);

        if (enabledUnits.length === 0) {
            return {
                text: 'Occupied · 0/0',
                showWarningIcon: true
            };
        }

        if (weightRuleWarning === 'weight') {
            return {
                text: 'Unavailable — By Weight',
                showWarningIcon: true
            };
        }

        const includedIndicators = enabledUnits
            .map(unit => (unit.id != null ? unitIndicators.get(unit.id) : undefined))
            .filter((indicator): indicator is UnitIndicator => Boolean(indicator?.includedInRoomSummary));

        const totalUnits = includedIndicators.length;

        if (totalUnits === 0 && hasBySize) {
            return {
                text: 'Unavailable — By Size',
                showWarningIcon: true
            };
        }

        if (totalUnits === 0) {
            return {
                text: 'Occupied · 0/0',
                showWarningIcon: true
            };
        }

        const availableUnits = includedIndicators.filter(indicator => indicator.remainingCapacity >= 1).length;
        const hasOverbookedUnits = includedIndicators.some(indicator => indicator.remainingCapacity < 0);

        if (availableUnits > 0) {
            return {
                text: `Available · ${availableUnits}/${totalUnits}`,
                showWarningIcon: hasOverbookedUnits
            };
        }

        return {
            text: `Occupied · 0/${totalUnits}`,
            showWarningIcon: true
        };
    }, [currentRoom, normalizedUnits, weightRuleWarning, unitIndicators, hasBySize]);

    const shouldShowBySizeWarning = useMemo(() => {
        if (!hasBySize) {
            return false;
        }

        if (selectedRoomUnit) {
            return isBySizeInvalidUnit(selectedRoomUnit);
        }

        return roomIndicator?.text === 'Unavailable — By Size';
    }, [hasBySize, selectedRoomUnit, roomIndicator]);

    const handleSelectRoomUnitId = (unitId: number | undefined) => {
        if (unitId == null) {
            setSelectedRoomUnit(undefined);
            onSelectRoomUnitId(undefined);
            return;
        }

        const roomUnit = normalizedUnits.find(unit => unit.id === unitId);

        if (!roomUnit) {
            setSelectedRoomUnit(undefined);
            onSelectRoomUnitId(undefined);
            return;
        }

        if (weightRuleWarning === 'weight') {
            dispatch(showWarningThunk('This room does not allow pets of this weight.'));
        } else if (hasBySize && isBySizeInvalidUnit(roomUnit)) {
            dispatch(showWarningThunk(SIZE_MISMATCH_WARNING_TEXT));
        } else {
            const indicator = roomUnit.id != null ? unitIndicators.get(roomUnit.id) : undefined;

            if (indicator && indicator.remainingCapacity < 1) {
                dispatch(showWarningThunk('This room is already at full capacity for the selected time'));
            }
        }

        setSelectedRoomUnit(roomUnit);
        onSelectRoomUnitId(roomUnit.id);
    };

    const conflictingTags = useMemo(() => {
        if (!currentRoom || !pet) return [];

        const petTags = [...(pet.positivePersonalitiesList ?? []), ...(pet.negativePersonalitiesList ?? [])];

        const roomRestrictedTags = currentRoom.restrictions?.petTagIds ?? [];

        return petTags.filter(tag => roomRestrictedTags.includes(tag));
    }, [currentRoom, pet]);

    const getUnitBadge = (unit: RoomUnit): UnitBadge | undefined => {
        if (unit.id == null) {
            return undefined;
        }

        const indicator = unitIndicators.get(unit.id);

        if (!indicator) {
            return undefined;
        }

        return {
            text: indicator.text,
            showWarningIcon: indicator.showWarningIcon
        };
    };

    return (
        <div className={clsx(styles.container, className)}>
            {boardingServices.map(boardingService => {
                const isSelected = selectedService?.serviceId === boardingService.serviceId;
                const offers = boardingService.offers ?? (isSelected ? selectedService?.offers : undefined);
                const appliedOffer = getBoardingAppliedOfferForStandardPricing(
                    boardingService.appliedOffer ?? (isSelected ? selectedService?.appliedOffer : undefined)
                );
                const pricingInput = {
                    price: boardingService.price,
                    catalogPrice: boardingService.catalogPrice,
                    beforePrice: boardingService.beforePrice,
                    offers,
                    appliedOffer
                };
                const originalNightlyPrice = getBoardingCatalogNightlyPrice(pricingInput);
                const appliedNightlyPrice = getBoardingStandardNightlyPrice(pricingInput);
                const hasMembership =
                    membershipItem != null ||
                    selectedService?.membershipItem != null ||
                    selectedService?.enrollment?.id != null;
                const shouldShowPromoAlert = isSelected && !hasMembership && hasBoardingRateRuleApplied(pricingInput);
                const catalogNightlyPrice = originalNightlyPrice;
                const isMembershipService =
                    membershipItem != null &&
                    membershipBoardingServiceId != null &&
                    boardingService.serviceId === membershipBoardingServiceId;
                const shouldShowMembershipWorseAlert =
                    isSelected &&
                    isMembershipService &&
                    isMembershipNightlyHigherThanAppliedRate(catalogNightlyPrice, appliedNightlyPrice, membershipItem);
                const membershipItemForPricing = membershipItem ?? selectedService?.membershipItem ?? undefined;
                const nightlyRateForTotal =
                    isSelected && nightlyPrice !== undefined && Number.isFinite(nightlyPrice)
                        ? nightlyPrice
                        : membershipItemForPricing != null
                        ? resolveBoardingNightlyPriceFromService(
                              { ...boardingService, selectedOffer: selectedService?.selectedOffer },
                              membershipItemForPricing
                          )
                        : appliedNightlyPrice;
                const stayTotal =
                    (nightlyRateForTotal ?? 0) *
                    getBoardingSelectableNights(
                        nights ?? 0,
                        boardingService.billingCalculation,
                        boardingService.quotedNights
                    );

                return (
                    <div
                        key={boardingService.serviceId}
                        className={clsx(styles.optionWrapper, {
                            [styles.selected]: isSelected
                        })}
                    >
                        <div className={styles.cardHeader}>
                            <div className={styles.cardHeaderLeft}>
                                <Radio
                                    inlineLabel={boardingService.serviceName}
                                    description={`Total $${stayTotal.toFixed(2)}`}
                                    checked={isSelected}
                                    className={styles.radioInHeader}
                                    onChange={() => onSelect(boardingService)}
                                />
                            </div>
                            <div className={styles.cardHeaderRight}>
                                {isSelected ? (
                                    <>
                                        <div
                                            className={clsx(styles.priceInputWrap, {
                                                [lockStyles.interactionLock]: !canEditPrices
                                            })}
                                        >
                                            <PriceField
                                                id="booking_boarding_nightly_price"
                                                aria-label="Nightly price"
                                                value={nightlyPrice}
                                                onChange={value =>
                                                    onNightlyPriceChange(value === undefined ? Number.NaN : value)
                                                }
                                                className={styles.compactPillInput}
                                                placeholder="$0.00"
                                                scale={2}
                                                precision={7}
                                            />
                                        </div>
                                        <span className={styles.nightSuffix}>night</span>
                                    </>
                                ) : (
                                    <span className={styles.readonlyNightlyLabel}>
                                        ${appliedNightlyPrice?.toFixed(2) ?? '0.00'}/night
                                    </span>
                                )}
                            </div>
                        </div>
                        {isSelected && nightlyPriceError && (
                            <span className={styles.priceErrorBelowHeader}>{nightlyPriceError}</span>
                        )}
                        {isSelected && savedCustomPrice.show && (
                            <SavedCustomPriceChoice
                                petName={savedCustomPrice.petName}
                                savedPrice={savedCustomPrice.savedPrice}
                                mode={savedCustomPrice.mode}
                                disabled={!canEditPrices}
                                onChange={mode => {
                                    const next = savedCustomPrice.applyMode(mode);
                                    onNightlyPriceChange(next.price);
                                    onUseSavedCustomPriceChange?.(
                                        next.useSavedCustomPrice,
                                        next.price,
                                        next.customPriceId
                                    );
                                }}
                            />
                        )}
                        {isSelected && (
                            <SaveCustomPriceCheckbox
                                className={styles.saveCustomPrice}
                                checked={!!saveCustomPrice}
                                disabled={!canEditPrices}
                                currentPrice={nightlyPrice}
                                defaultPrice={catalogNightlyPrice}
                                onChange={onSaveCustomPriceChange}
                            />
                        )}

                        {isSelected && (
                            <div className={styles.extraInfo}>
                                {shouldShowMembershipWorseAlert && (
                                    <AlertNewBooking
                                        className={styles.promoRateAlert}
                                        bWidth="2px"
                                        icon={faExclamationTriangle}
                                        iconSize="lg"
                                        iconColor="#EAB464"
                                        bgColor="#F8F5F1"
                                        bdColor="#BCB8AE"
                                    >
                                        <span>The selected membership does not offer the best available rate.</span>
                                    </AlertNewBooking>
                                )}
                                {shouldShowPromoAlert && (
                                    <AlertNewBooking
                                        className={styles.promoRateAlert}
                                        bWidth="2px"
                                        icon={faExclamationTriangle}
                                        iconSize="lg"
                                        iconColor="#EAB464"
                                        bgColor="#F8F5F1"
                                        bdColor="#BCB8AE"
                                    >
                                        <span>
                                            Rate rule applied – ${appliedNightlyPrice.toFixed(2)}/night (Default $
                                            {originalNightlyPrice.toFixed(2)}).
                                        </span>
                                    </AlertNewBooking>
                                )}
                                <hr
                                    className={
                                        shouldShowMembershipWorseAlert || shouldShowPromoAlert
                                            ? styles.promoDivider
                                            : undefined
                                    }
                                />
                                <PickDropHourSelector
                                    from={from}
                                    to={to}
                                    onSelect={handlePeriodChange}
                                />
                                <hr />

                                {onNotesChange && (
                                    <div className={styles.notesSection}>
                                        <Notes
                                            title="Notes"
                                            placeholder="Add notes for this booking..."
                                            notes={notes ?? ''}
                                            onChangeNote={onNotesChange}
                                        />
                                    </div>
                                )}
                                <hr />

                                <BoardingUnitSelectorSection
                                    service={boardingService}
                                    room={room}
                                    petId={petId}
                                    roomUnitId={roomUnitId}
                                    onSelectRoomUnitId={onSelectRoomUnitId}
                                    isLoading={isLoading}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default StandardRateSelector;
